import type {
  AnalyticsAttribution,
  AnalyticsPendingPurchase,
  AnalyticsUserProperties,
} from './data-layer.types';

const SESSION_START_KEY = 'handel.analytics.sessionStart';
const ATTRIBUTION_KEY = 'handel.analytics.attribution';
const USER_PROPERTIES_KEY = 'handel.analytics.userProperties';
const DNI_RETRY_KEY = 'handel.analytics.dniRetry';
const CHECKOUT_STARTED_KEY = 'checkout_started';
const TRACKED_PURCHASES_KEY = 'handel.analytics.trackedPurchases';
const PENDING_PURCHASE_KEY = 'handel.analytics.pendingPurchase';
const TRACKED_PURCHASES_LIMIT = 50;

type StorageKind = 'session' | 'local';

export function readSessionStart(now: number): number {
  const stored = readNumber(SESSION_START_KEY);
  if (stored > 0) return stored;
  writeValue(SESSION_START_KEY, String(now));
  return now;
}

export function readAttribution(): AnalyticsAttribution | null {
  const value = readJson(ATTRIBUTION_KEY);
  return isRecord(value) ? (value as unknown as AnalyticsAttribution) : null;
}

export function writeAttribution(value: AnalyticsAttribution): void {
  writeValue(ATTRIBUTION_KEY, JSON.stringify(value));
}

export function readUserProperties(): AnalyticsUserProperties | null {
  const value = readJson(USER_PROPERTIES_KEY);
  return isRecord(value) ? (value as unknown as AnalyticsUserProperties) : null;
}

export function writeUserProperties(value: AnalyticsUserProperties): void {
  writeValue(USER_PROPERTIES_KEY, JSON.stringify(value));
}

export function readDniRetryCount(): number {
  return Math.max(0, readNumber(DNI_RETRY_KEY));
}

export function incrementDniRetryCount(): number {
  const next = readDniRetryCount() + 1;
  writeValue(DNI_RETRY_KEY, String(next));
  return next;
}

export function markCheckoutStarted(signature: string): boolean {
  if (readValue(CHECKOUT_STARTED_KEY) === signature) return false;
  writeValue(CHECKOUT_STARTED_KEY, signature);
  return true;
}

export function clearCheckoutStarted(): void {
  removeValue(CHECKOUT_STARTED_KEY);
}

export function purchaseStorageKey(transactionId: string): string {
  return `purchase_${transactionId}`;
}

export function hasTrackedPurchase(transactionId: string): boolean {
  if (readTrackedPurchaseIds().includes(transactionId)) return true;
  // Legacy per-session flag written before tracked ids moved to localStorage.
  return Boolean(readValue(purchaseStorageKey(transactionId), 'session'));
}

export function markTrackedPurchase(transactionId: string): void {
  const ids = readTrackedPurchaseIds().filter((id) => id !== transactionId);
  ids.push(transactionId);
  writeValue(TRACKED_PURCHASES_KEY, JSON.stringify(ids.slice(-TRACKED_PURCHASES_LIMIT)), 'local');
}

export function readPendingPurchase(): AnalyticsPendingPurchase | null {
  const value = readJson(PENDING_PURCHASE_KEY);
  if (!isRecord(value) || !Array.isArray(value['items'])) return null;
  return value as unknown as AnalyticsPendingPurchase;
}

export function writePendingPurchase(value: AnalyticsPendingPurchase): void {
  writeValue(PENDING_PURCHASE_KEY, JSON.stringify(value));
}

export function clearPendingPurchase(): void {
  removeValue(PENDING_PURCHASE_KEY);
}

function readTrackedPurchaseIds(): string[] {
  const value = readJson(TRACKED_PURCHASES_KEY, 'local');
  return Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : [];
}

function readJson(key: string, kind: StorageKind = 'session'): unknown {
  const raw = readValue(key, kind);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

function readNumber(key: string): number {
  const value = Number(readValue(key) ?? '');
  return Number.isFinite(value) ? value : 0;
}

function readValue(key: string, kind: StorageKind = 'session'): string | null {
  try {
    return storageOf(kind).getItem(key);
  } catch {
    return null;
  }
}

function writeValue(key: string, value: string, kind: StorageKind = 'session'): void {
  try {
    storageOf(kind).setItem(key, value);
  } catch {
    // Private mode / blocked storage should not break the storefront.
  }
}

function removeValue(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Private mode / blocked storage should not break the storefront.
  }
}

function storageOf(kind: StorageKind): Storage {
  return kind === 'local' ? localStorage : sessionStorage;
}
